import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Crown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { PayPalScriptProvider, PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { useProfile } from "@/context/ProfileContext";
import { errorMessage, publicApi } from "@/lib/api";

const PLANS = [
  { id: "creator", name: "Creator", price: "4.99", perks: ["Upload AI music & videos", "Creator badge on your profile", "Chart eligibility"] },
  { id: "pro", name: "Pro", price: "9.99", perks: ["Everything in Creator", "Pro badge on your profile", "Priority placement (coming soon)"], featured: true },
];

const PayButtons = ({ plan }) => {
  const { request } = useProfile();
  const navigate = useNavigate();
  const [{ isPending }] = usePayPalScriptReducer();
  return (
    <div data-testid={`paypal-buttons-${plan}`} className="rounded-xl bg-white p-3">
      {isPending && <Loader2 className="mx-auto h-5 w-5 animate-spin text-slate-500" />}
      <PayPalButtons
        style={{ layout: "vertical", shape: "pill", label: "subscribe" }}
        createSubscription={async () => (await request("post", "/payments/paypal/subscriptions", { plan, origin_url: window.location.origin })).id}
        onApprove={async (data) => navigate(`/payment/success?subscription_id=${data.subscriptionID}`)}
        onCancel={() => toast("Checkout canceled — no payment was taken.")}
        onError={(e) => toast.error(errorMessage(e) === "Something went wrong" ? "PayPal checkout failed. Please try again." : errorMessage(e))}
      />
    </div>
  );
};

const PlanCard = ({ plan, open, onPick }) => (
  <div data-testid={`plan-card-${plan.id}`} className={`rounded-2xl border p-5 space-y-4 bg-nw-surface/70 ${plan.featured ? "border-nw-red/60 shadow-[0_0_30px_rgba(255,30,39,0.15)]" : "border-white/10"}`}>
    <div className="flex items-center justify-between">
      <p className="font-display text-xl font-bold text-white">{plan.name}</p>
      {plan.featured && <Crown className="h-5 w-5 text-nw-red" />}
    </div>
    <p className="font-display text-3xl font-extrabold chrome-text">${plan.price}<span className="text-sm font-medium text-slate-400"> / month</span></p>
    <ul className="space-y-1.5 text-sm text-slate-300">
      {plan.perks.map((p) => <li key={p} className="flex items-start gap-2"><Check className="h-4 w-4 mt-0.5 text-nw-red shrink-0" />{p}</li>)}
    </ul>
    {open ? <PayButtons plan={plan.id} /> : (
      <button data-testid={`plan-subscribe-${plan.id}`} onClick={() => onPick(plan.id)}
        className={`${plan.featured ? "btn-red" : "btn-chrome"} w-full py-2.5 text-sm`}>
        Subscribe to {plan.name}
      </button>
    )}
  </div>
);

export const PlanPicker = ({ title = "Choose your plan", note }) => {
  const [clientId, setClientId] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    publicApi.get("/payments/config").then(({ data }) => setClientId(data.client_id)).catch(() => toast.error("Payments are unavailable right now."));
  }, []);
  return (
    <div data-testid="plan-picker" className="space-y-4">
      <div className="space-y-1">
        <h2 className="font-display text-2xl font-bold text-white">{title}</h2>
        {note && <p className="text-sm text-slate-400">{note}</p>}
      </div>
      {clientId ? (
        <PayPalScriptProvider options={{ clientId, vault: true, intent: "subscription", components: "buttons" }}>
          <div className="grid gap-4 sm:grid-cols-2">
            {PLANS.map((p) => <PlanCard key={p.id} plan={p} open={open === p.id} onPick={setOpen} />)}
          </div>
        </PayPalScriptProvider>
      ) : <Loader2 data-testid="plan-picker-loading" className="h-6 w-6 animate-spin text-nw-red" />}
      <p className="text-xs text-slate-500">Pay with PayPal or a credit/debit card. Billed monthly — cancel any time from your profile.</p>
    </div>
  );
};
